import React from "react";
import {
  Eye,
  AlertTriangle,
  Search,
  ArrowRightCircle,
  Leaf,
  Bug,
  Droplets,
  Mountain,
  Sprout,
  Sun,
  Flower2,
  LayoutGrid,
  Shield,
  Lightbulb,
  SprayCan,
  Recycle,
  HeartPulse,
  Info,
  Compass,
} from "lucide-react";

const ICONS = {
  "Plant health": HeartPulse,
  "Possible pest activity": Bug,
  "Possible disease symptoms": AlertTriangle,
  "Irrigation/water": Droplets,
  "Soil/root-zone concerns": Mountain,
  "Turf concerns": Sprout,
  "Environmental/site conditions": Sun,
  "Planting & Color": Flower2,
  "Landscape Beds": LayoutGrid,
  "Plant Health": HeartPulse,
  Turf: Sprout,
  Irrigation: Droplets,
  "Privacy/Screening": Shield,
  "Landscape Lighting": Lightbulb,
  "Property Cleaning": SprayCan,
  "Sustainable Improvements": Recycle,
  "Planting and Color": Flower2,
  "Privacy and Screening": Shield,
  "Mulch, Stone and Edging": LayoutGrid,
};

const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function Section({ title, icon: Icon, tone, testId, children }) {
  const t = tone === "blue" ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700";
  return (
    <section data-testid={testId} className="rounded-2xl bg-white border border-slate-200 p-5">
      <div className="flex items-center gap-3">
        <div className={`h-9 w-9 rounded-xl grid place-items-center ${t}`}>
          <Icon size={18} />
        </div>
        <h2 className="font-display text-lg font-bold text-[#092C15]">{title}</h2>
      </div>
      <div className="mt-3 text-sm text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}

const Bullets = ({ items }) => (
  <ul className="space-y-1.5">
    {(items || []).map((it, i) => (
      <li key={i} className="flex gap-2">
        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

function CategoryGroups({ groups, itemsKey, tone }) {
  const head = tone === "blue" ? "text-sky-800" : "text-emerald-800";
  return (
    <div className="space-y-4">
      {(groups || []).map((g) => {
        const Icon = ICONS[g.category] || Leaf;
        return (
          <div key={g.category} data-testid={`category-${slug(g.category)}`}>
            <div className={`flex items-center gap-2 font-semibold ${head}`}>
              <Icon size={15} /> {g.category}
            </div>
            <div className="mt-1.5 pl-6">
              <Bullets items={g[itemsKey]} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function DiagnoseReport({ a }) {
  return (
    <div className="space-y-3" data-testid="diagnose-report">
      {a.summary && <p className="text-slate-700 text-sm leading-relaxed" data-testid="report-summary">{a.summary}</p>}
      <Section title="Dr. Verdant's Observations" icon={Eye} tone="blue" testId="report-observations">
        <Bullets items={a.observations} />
      </Section>
      {a.areas_of_concern?.length > 0 && (
        <Section title="Areas of Concern" icon={AlertTriangle} tone="blue" testId="report-concerns">
          <CategoryGroups groups={a.areas_of_concern} itemsKey="items" tone="blue" />
        </Section>
      )}
      <Section title="What We Need to Investigate" icon={Search} tone="blue" testId="report-investigate">
        <Bullets items={a.investigate} />
      </Section>
      <Section title="Recommended Next Step" icon={ArrowRightCircle} tone="blue" testId="report-next-steps">
        <div className="space-y-2.5">
          {(a.next_steps || []).map((s, i) => (
            <div key={i} className="rounded-xl bg-sky-50/70 border border-sky-100 p-3">
              <div className="font-semibold text-[#092C15]">{s.title}</div>
              <div className="text-slate-600 mt-0.5">{s.detail}</div>
            </div>
          ))}
        </div>
      </Section>
      <div data-testid="report-disclaimer" className="flex gap-2 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
        <Info size={14} className="mt-0.5 shrink-0" />
        <span>
          {a.confidence_note ? `${a.confidence_note} ` : ""}This is a preliminary, photo-based assessment — not a confirmed
          diagnosis. Confirmation may require a GreenFlow professional evaluation or testing.
        </span>
      </div>
    </div>
  );
}

export function ImproveReport({ a }) {
  return (
    <div className="space-y-3" data-testid="improve-report">
      {a.summary && <p className="text-slate-700 text-sm leading-relaxed" data-testid="report-summary">{a.summary}</p>}
      <Section title="What We See" icon={Eye} tone="green" testId="report-what-we-see">
        <Bullets items={a.what_we_see} />
      </Section>
      {a.opportunities?.length > 0 && (
        <Section title="GreenFlow Enhancement Opportunities" icon={Sprout} tone="green" testId="report-opportunities">
          <CategoryGroups groups={a.opportunities} itemsKey="concepts" tone="green" />
        </Section>
      )}
      <Section title="GreenFlow's Recommended Direction" icon={Compass} tone="green" testId="report-direction">
        <p>{a.recommended_direction}</p>
      </Section>
    </div>
  );
}
